import {
  type CSSProperties,
  type FC,
  type MouseEventHandler,
  type RefObject,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import { type TaskStatus, type TaskV1 } from '@/model/Task.model';
import { type TaskPosition } from '@/components/Tasks/Calendar/Views/Day/GanttView.utils';
import type { ColorDetail } from '@/constants/colors';
import { _notNil } from '@/littledash';
import cn from 'classnames';
import styles from '@/components/Tasks/Calendar/Views/Day/GanttView.module.scss';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { DateTimeRenderFormat } from '@/utils/Date.utils';
import { type components } from '@/generated/internal-api/openapi-types';
import { MdOutlineCircle, MdPerson, MdPets, MdSlowMotionVideo } from 'react-icons/md';
import { RiCheckboxCircleFill } from 'react-icons/ri';
import { TbCancel } from 'react-icons/tb';

const elements = new Map<string, { card: HTMLDivElement; title: HTMLDivElement }>();
const resizeObserver =
  typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const title = elements.get(entry.target.getAttribute('data-task-observer-id') ?? '')?.title;
          if (_notNil(title)) {
            if (title.getBoundingClientRect().width > entry.contentRect.width - 21) {
              title.style.maxWidth = `${entry.contentRect.width - 21}px`;
              title.setAttribute('data-tooltip-content', title.innerText);
            } else {
              title.style.removeProperty('max-width');
              title.removeAttribute('data-tooltip-content');
            }
          }
        });
      })
    : null;

const useResizeObserver = (cardRef: RefObject<HTMLDivElement>, titleRef: RefObject<HTMLDivElement>) => {
  const id = useId();
  useEffect(() => {
    if (_notNil(cardRef.current) && _notNil(titleRef.current)) {
      elements.set(id, { card: cardRef.current, title: titleRef.current });
      cardRef.current.setAttribute('data-task-observer-id', id);
      resizeObserver?.observe(cardRef.current);
    }
    return () => {
      const card = elements.get(id)?.card;
      elements.delete(id);
      if (_notNil(card)) {
        resizeObserver?.unobserve(card);
      }
    };
  }, [id, cardRef, titleRef]);
};

export const StatusIcon: FC<{ status: components['schemas']['TaskStatus.schema'] }> = ({ status }) => {
  switch (status) {
    case 'to_do':
      return <MdOutlineCircle />;
    case 'in_progress':
      return <MdSlowMotionVideo />;
    case 'done':
      return <RiCheckboxCircleFill className="green" />;
    case 'cancelled':
      return <TbCancel className="gray" />;
    default:
      return null;
  }
};
const statusText: Record<TaskStatus, string> = {
  to_do: 'To Do',
  in_progress: 'In Progress',
  done: 'Done',
  cancelled: 'Cancelled',
} as const;

export const TaskCard: FC<{
  task: TaskV1;
  timezone: string;
  position?: TaskPosition;
  color?: ColorDetail;
  onClick?: MouseEventHandler<HTMLDivElement>;
}> = ({ task, timezone, position, color, onClick }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  useResizeObserver(cardRef, titleRef);
  const gridStyle = useMemo<CSSProperties>(() => {
    if (_notNil(position)) {
      return {
        gridRow: `${position.row + 1}/${position.row + 1}`,
        gridColumn: `${position.start + 1}/${position.end + 1}`,
        borderColor: color?.color,
      };
    }
    return {};
  }, [position, color]);
  return (
    <div
      ref={cardRef}
      className="ba bg-near-white"
      style={gridStyle}
      onClick={onClick}
      data-test-component="TaskCard"
      data-test-element="container"
      data-test-key={task.id}
    >
      <div className={cn('flex flex-row', styles.TaskCard)} style={{ backgroundColor: color?.background?.color }}>
        <div className={cn('flex flex-row', styles.Container)}>
          <div className="pa2">
            <div className={styles.ColorChip} style={{ backgroundColor: color?.color ?? 'white' }}></div>
          </div>
          <div className="flex flex-column pv2">
            <div
              ref={titleRef}
              data-tooltip-id="global-tooltip-id"
              className={cn('f7 near-black fw5', styles.TitleRow)}
            >
              {task.title}
            </div>
            <div className={cn('f8 dark-gray', styles.TimeRow)}>
              <span>
                <DateTimeRenderer value={task.duration.start} timezone={timezone} format={DateTimeRenderFormat.Time} />
              </span>
              <span> - </span>
              <span>
                <DateTimeRenderer value={task.duration.end} timezone={timezone} format={DateTimeRenderFormat.Time} />
              </span>
            </div>
            <div className={cn('f8 dark-gray flex flex-row items-center', styles.StatusRow)}>
              <span
                className="flex flex-row items-center mr1"
                data-tooltip-id="global-tooltip-id"
                data-tooltip-html={statusText?.[task.status]}
                data-tooltip-place="bottom"
              >
                <StatusIcon status={task.status} />
              </span>
              <span
                className="flex flex-row items-center mr1"
                data-tooltip-id="global-tooltip-id"
                data-tooltip-html={`Affects ${task.target?.animal_count ?? 0} animals`}
                data-tooltip-place="bottom"
              >
                <MdPets />
                {task.target?.animal_count ?? 0}
              </span>
              <span
                className="flex flex-row items-center"
                data-tooltip-id="global-tooltip-id"
                data-tooltip-html={task.assignees?.map((a) => a.name).join('<br />')}
                data-tooltip-place="bottom"
              >
                <MdPerson />
                {task.assignees?.length ?? 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
